/**
 * Phaser hit-tests a point after adding `displayOrigin`.
 * Buttons are centered (origin 0.5), so a click on the visual center arrives
 * at `(width / 2, height / 2)`, not `(0, 0)`.
 *
 * A rectangle centered on the origin, `(-w/2, -h/2, w, h)`, therefore only
 * covers the top-left quarter of the drawn button. The rectangle that covers
 * the drawn face starts at `(0, 0)` and is `width` by `height`. Padding and
 * the 88px minimum grow that rectangle equally on every side.
 */

export const MIN_TOUCH = 88;
export const TOUCH_PAD = 8;

export interface HitRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function coverHitArea(
  drawnW: number,
  drawnH: number,
  frameW = drawnW,
  frameH = drawnH,
  scaleX = 1,
  scaleY = 1,
): HitRect {
  const absX = Math.abs(scaleX) || 1;
  const absY = Math.abs(scaleY) || 1;
  const hitDisplayW = Math.max(drawnW + TOUCH_PAD * 2, MIN_TOUCH);
  const hitDisplayH = Math.max(drawnH + TOUCH_PAD * 2, MIN_TOUCH);
  const hitW = Math.ceil(hitDisplayW / absX);
  const hitH = Math.ceil(hitDisplayH / absY);
  return {
    x: frameW / 2 - hitW / 2,
    y: frameH / 2 - hitH / 2,
    w: hitW,
    h: hitH,
  };
}

/** Same test Phaser runs: shift by the display origin, then rectangle-contains. */
export function hitContains(
  area: HitRect,
  localX: number,
  localY: number,
  displayOriginX: number,
  displayOriginY: number,
): boolean {
  const x = localX + displayOriginX;
  const y = localY + displayOriginY;
  return area.x <= x && x <= area.x + area.w && area.y <= y && y <= area.y + area.h;
}
